/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Divider,
  useTheme,
  TextField as MuiTextField,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  ListItemText,
  FormHelperText,
  Chip,
} from "@mui/material";
import Sidebar from "../../components/sidebar/Sidebar";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { useDispatch, useSelector } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import dayjs from "dayjs";
import { components } from "../../types/api";
import { ApiErrorResponse } from "../../types/campaign";
import {
  useGetCampaignByIdQuery,
  useUpdateCampaignMutation,
  useGetCampaignFormatsQuery,
} from "../../store/apis/campaignApi";
import { useGetCampaignInsertionOrdersQuery } from "../../store/apis/dv360Api";
import { getPlatformDisplayName } from "../../utils/iconUtils";
import { AssignedCampaign } from "../../types/campaign";
import CampaignMergeEditor from "../../components/campaign/CampaignMergeEditor";
import * as Sentry from "@sentry/react";
import { useGetInsertionOrdersByCampaignQuery } from "../../store/apis/insertionOrderApi";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import PageContainer from "../../components/layout/PageContainer";

const getInsertionOrderKey = (
  order?: components["schemas"]["InsertionOrderType"]
): string | undefined => {
  if (!order) {
    return undefined;
  }

  if (order.insertionOrderId) {
    return order.insertionOrderId;
  }

  const legacyId = (order as { id?: string | null | undefined })?.id;
  if (legacyId) {
    return legacyId;
  }

  return undefined;
};

// Define types for the campaign data using the API types
type Platform = components["schemas"]["_36_Enums.Platform"];
type CampaignGoal = components["schemas"]["_36_Enums.CampaignGoal"];
type InternalPlatform = components["schemas"]["_36_Enums.InternalPlatform"];

// Define a type for the form's internal campaign data (using dayjs for date handling)
interface InternalCampaignData {
  id?: string;
  displayName: string;
  internalPlatform?: InternalPlatform;
  platform?: Platform;
  internalId?: string;
  advertiserId?: string;
  margin?: number;
  dv360InsertionOrderId?: string; // Add this line
  startDate?: dayjs.Dayjs | null;
  endDate?: dayjs.Dayjs | null;
  timezone?: string;
}

// Define a type for the main campaign data
interface CampaignData {
  displayName: string;
  // Read-only fields
  platform?: Platform;
  goal?: CampaignGoal;
  format?: components["schemas"]["CampaignFormats"];
  startDate?: dayjs.Dayjs | null;
  endDate?: dayjs.Dayjs | null;
  // Array of internal campaigns
  internalCampaigns: InternalCampaignData[];
}

const EditCampaign: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const collapsed = useSelector(selectSidebarCollapsed);
  const theme = useTheme();

  // Form state
  const [formData, setFormData] = useState<CampaignData>({
    displayName: "",
    platform: undefined,
    goal: "CPC",
    format: undefined,
    startDate: null,
    endDate: null,
    internalCampaigns: [],
  });

  // State for internal campaigns (for the merge editor)
  const [internalCampaigns, setInternalCampaigns] = useState<
    AssignedCampaign[]
  >([]);

  const [dv360InsertionOrders, setDv360InsertionOrders] = useState<
    components["schemas"]["InsertionOrderType"][]
  >([]);
  const [selectedDv360InsertionOrderIds, setSelectedDv360InsertionOrderIds] =
    useState<string[]>([]);
  const [selectedDv360InsertionOrders, setSelectedDv360InsertionOrders] =
    useState<components["schemas"]["SelectedInsertionOrderInfo"][]>([]);
  const [dv360InsertionOrderError, setDv360InsertionOrderError] =
    useState<string>("");
  const [selectedDv360CampaignId, setSelectedDv360CampaignId] = useState<
    string | null
  >(null);

  // State for campaign merges
  const [internalCampaignMerges, setInternalCampaignMerges] = useState<
    {
      id?: string;
      name: string;
      description?: string;
      campaignId?: string;
      createdAt?: string;
      updatedAt?: string;
      internalCampaignIds: string[];
    }[]
  >([]);

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch campaign data
  const { data: campaignResponse, isLoading: isLoadingCampaign } =
    useGetCampaignByIdQuery(
      { campaignId: campaignId || "" },
      { skip: !campaignId }
    );

  // Fetch campaign formats for DV360 dropdown
  const { data: campaignFormatsData, isLoading: isLoadingCampaignFormats } =
    useGetCampaignFormatsQuery();

  // Fetch insertion orders for the campaign
  const { data: insertionOrdersByCampaign } =
    useGetInsertionOrdersByCampaignQuery(campaignId || "", {
      skip: !campaignId,
    });
  console.log("Insertion orders for campaign:", insertionOrdersByCampaign);
  const hasIOs = (insertionOrdersByCampaign?.data?.length ?? 0) > 0;

  // Fetch DV360 insertion orders with selected state for editing
  const {
    data: dv360CampaignInsertionOrders,
    isLoading: isLoadingDv360InsertionOrders,
    isError: isDv360InsertionOrdersError,
  } = useGetCampaignInsertionOrdersQuery(campaignId || "", {
    skip: !campaignId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  console.log("DV360 insertion orders:", dv360CampaignInsertionOrders?.data);

  const mapIdsToSelectedInfo = (
    ids: string[],
    orders: components["schemas"]["InsertionOrderType"][],
    fallbackInfo?: components["schemas"]["SelectedInsertionOrderInfo"][]
  ): components["schemas"]["SelectedInsertionOrderInfo"][] => {
    const fallbackMap = new Map(
      (fallbackInfo ?? []).map((info) => [
        info.insertionOrderId,
        info.displayName,
      ])
    );

    return ids.map((id) => {
      const matchedOrder = orders.find(
        (order) => getInsertionOrderKey(order) === id
      );

      if (matchedOrder) {
        return {
          insertionOrderId: id,
          displayName: matchedOrder.displayName || fallbackMap.get(id) || id,
        };
      }

      return {
        insertionOrderId: id,
        displayName: fallbackMap.get(id) || id,
      };
    });
  };

  useEffect(() => {
    const responseData = dv360CampaignInsertionOrders?.data;
    if (!responseData) {
      return;
    }

    const remoteInsertionOrders = responseData.insertionOrders ?? [];
    const selectedInsertionOrdersInfo =
      responseData.selectedInsertionOrders ?? [];

    const mergedOrdersMap = new Map<
      string,
      components["schemas"]["InsertionOrderType"]
    >();

    remoteInsertionOrders.forEach((order) => {
      const key = getInsertionOrderKey(order);
      if (!key) {
        return;
      }
      mergedOrdersMap.set(key, order);
    });

    selectedInsertionOrdersInfo.forEach((orderInfo) => {
      if (!orderInfo?.insertionOrderId) {
        return;
      }

      if (mergedOrdersMap.has(orderInfo.insertionOrderId)) {
        const existingOrder = mergedOrdersMap.get(orderInfo.insertionOrderId)!;
        if (!existingOrder.displayName && orderInfo.displayName) {
          mergedOrdersMap.set(orderInfo.insertionOrderId, {
            ...existingOrder,
            displayName: orderInfo.displayName,
          });
        }
        return;
      }

      mergedOrdersMap.set(orderInfo.insertionOrderId, {
        insertionOrderId: orderInfo.insertionOrderId,
        displayName: orderInfo.displayName ?? orderInfo.insertionOrderId,
      } as components["schemas"]["InsertionOrderType"]);
    });

    const nextOrderList = Array.from(mergedOrdersMap.values());
    setDv360InsertionOrders(nextOrderList);

    const nextSelectedIds = responseData.selectedInsertionOrderIds ?? [];
    setSelectedDv360InsertionOrderIds(nextSelectedIds);
    setSelectedDv360InsertionOrders(
      mapIdsToSelectedInfo(
        nextSelectedIds,
        nextOrderList,
        selectedInsertionOrdersInfo
      )
    );
  }, [dv360CampaignInsertionOrders?.data]);

  // Log the raw campaign response when it changes
  useEffect(() => {
    if (campaignResponse) {
      console.log("Raw campaign response:", campaignResponse);
      // Use type assertion to access the merges property
      const campaignData = campaignResponse.data as any;
      console.log("Has merges data?", !!campaignData?.merges);
      if (campaignData?.merges) {
        console.log("Raw merges data:", campaignData.merges);
      }
    }
  }, [campaignResponse]);

  // Update campaign mutation
  const [
    updateCampaign,
    { isLoading: isUpdating, data: updateData, error: updateError },
  ] = useUpdateCampaignMutation();

  // First, process the existing merges from the API response
  useEffect(() => {
    console.log("Processing merges effect triggered");
    if (campaignResponse?.data) {
      console.log("Campaign data available for merges processing");

      // Use type assertion to access the merges property
      const campaignData = campaignResponse.data as any;

      if (campaignData.merges) {
        console.log("Found merges in response:", campaignData.merges);

        // Map the API response to our internal state format
        const merges = campaignData.merges.map((merge: any) => {
          console.log("Processing merge:", merge);
          console.log("Merge internal campaigns:", merge.internalCampaigns);

          return {
            id: merge.id,
            name: merge.name,
            description: merge.description || "",
            campaignId: merge.campaignId,
            createdAt: merge.createdAt,
            updatedAt: merge.updatedAt,
            internalCampaignIds:
              merge.internalCampaigns?.map((ic: any) => {
                console.log(
                  "Adding campaign ID to merge:",
                  ic.internalCampaignId
                );
                return ic.internalCampaignId;
              }) || [],
          };
        });

        console.log("Setting internalCampaignMerges state with:", merges);
        setInternalCampaignMerges(merges);
      } else {
        console.log("No merges found in response");
      }
    } else {
      console.log("No campaign data available yet for merges processing");
    }
  }, [campaignResponse?.data]);

  useEffect(() => {
    if (selectedDv360CampaignId) {
      return;
    }

    const sourceCampaign = internalCampaigns.find(
      (campaign) => campaign.platform === "DV360" && campaign.internalId
    );

    if (sourceCampaign?.internalId) {
      setSelectedDv360CampaignId(sourceCampaign.internalId);
      // Map back to internalId
      const formDv360 = formData.internalCampaigns?.find(
        (ic) => ic.internalPlatform === "DV360" && ic.internalId
      );
      if (formDv360?.internalId) {
        setSelectedDv360CampaignId(formDv360.internalId);
      }
    }
  }, [internalCampaigns, formData.internalCampaigns, selectedDv360CampaignId]);

  // Then initialize form with campaign data after merges are processed
  useEffect(() => {
    if (campaignResponse?.data) {
      // Cast the campaign data to our expected structure
      // This is necessary because the API schema doesn't match the actual data
      const campaign = campaignResponse.data;

      // Also populate the internalCampaigns array for the merge editor
      if (Array.isArray(campaign.internalCampaigns)) {
        const assignedCampaigns: AssignedCampaign[] =
          campaign.internalCampaigns.map((ic) => {
            // Map internal platform to platform (DV360 or NATADS)
            // Any platform that starts with DV360 maps to DV360, others map to NATADS
            const mappedPlatform = (ic.internalPlatform || "").startsWith(
              "DV360"
            )
              ? "DV360"
              : "NATADS";

            return {
              id: ic.id || "",
              name: ic.displayName || "",
              // Use the mapped platform that conforms to the Platform type
              platform: mappedPlatform,
              startDate: ic.startDate || "",
              endDate: ic.endDate || "",
              // Add missing required properties
              originalPlatform: ic.internalPlatform || "OUTBRAIN",
              status: "ACTIVE", // Default status
              // Additional properties from Campaign type that might be needed
              displayName: ic.displayName || "",
              internalPlatform: ic.internalPlatform,
              margin: ic.margin,
              internalId: ic.internalId || undefined,
            };
          });
        setInternalCampaigns(assignedCampaigns);
        console.log("Loaded internal campaigns:", assignedCampaigns); // Debug log
      }

      // Map the API response to our form data structure
      setFormData({
        displayName: campaign.displayName || "",
        // Read-only fields
        platform: campaign.platform as Platform,
        goal: campaign.goal as CampaignGoal,
        format: campaign.format,
        startDate: campaign.startDate ? dayjs(campaign.startDate) : null,
        endDate: campaign.endDate ? dayjs(campaign.endDate) : null,
        // Map internal campaigns
        internalCampaigns: Array.isArray(campaign.internalCampaigns)
          ? campaign.internalCampaigns.map((internalCampaign) => ({
              id: internalCampaign.id,
              displayName: internalCampaign.displayName || "",
              internalPlatform: internalCampaign.internalPlatform,
              platform: (internalCampaign.internalPlatform || "").startsWith(
                "DV360"
              )
                ? "DV360"
                : (campaign.platform as Platform | undefined),
              margin: internalCampaign.margin,
              startDate: internalCampaign.startDate
                ? dayjs(internalCampaign.startDate)
                : null,
              endDate: internalCampaign.endDate
                ? dayjs(internalCampaign.endDate)
                : null,
              internalId: internalCampaign.internalId ?? null,
              // Use type assertion for API response fields
              timezone: (internalCampaign as unknown as { timezone?: string })
                .timezone,
              advertiserId:
                (
                  internalCampaign as unknown as {
                    advertiserId?: string | null;
                  }
                ).advertiserId ?? undefined,
            }))
          : [],
      });
    }
  }, [campaignResponse?.data]);

  // Handle input changes for the campaign name
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Handle input changes for internal campaigns
  const handleInternalCampaignChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>,
    internalCampaignIndex: number
  ) => {
    const { name, value } = e.target;
    if (!name) return;

    const updatedInternalCampaigns = [...formData.internalCampaigns];
    updatedInternalCampaigns[internalCampaignIndex] = {
      ...updatedInternalCampaigns[internalCampaignIndex],
      [name]: value,
    };

    setFormData({
      ...formData,
      internalCampaigns: updatedInternalCampaigns,
    });
  };

  // Handle format dropdown change
  const handleFormatChange = (e: SelectChangeEvent<string>) => {
    setFormData({
      ...formData,
      format: e.target.value as components["schemas"]["CampaignFormats"],
    });
  };

  // Handle campaign end date change
  const handleCampaignEndDateChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const newEndDate = value ? dayjs(value) : null;

    setFormData({
      ...formData,
      endDate: newEndDate,
    });

    // Clear any existing error for this field
    if (errors.campaignEndDate) {
      const newErrors = { ...errors };
      delete newErrors.campaignEndDate;
      setErrors(newErrors);
    }

    // Validate the new end date immediately
    if (newEndDate) {
      const error = validateEndDate(newEndDate);
      if (error) {
        setErrors((prev) => ({
          ...prev,
          campaignEndDate: error,
        }));
      }
    }
  };

  // Handle internal campaign end date change
  const handleInternalCampaignEndDateChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    internalCampaignIndex: number
  ) => {
    const value = e.target.value;
    const newEndDate = value ? dayjs(value) : null;

    const updatedInternalCampaigns = [...formData.internalCampaigns];
    updatedInternalCampaigns[internalCampaignIndex] = {
      ...updatedInternalCampaigns[internalCampaignIndex],
      endDate: newEndDate,
    };

    setFormData({
      ...formData,
      internalCampaigns: updatedInternalCampaigns,
    });

    // Clear any existing error for this field
    const errorKey = `internalCampaign_${internalCampaignIndex}_endDate`;
    if (errors[errorKey]) {
      const newErrors = { ...errors };
      delete newErrors[errorKey];
      setErrors(newErrors);
    }

    // Validate the new end date immediately
    if (newEndDate) {
      // Get the original end date from the API response
      let originalInternalEndDate = null;
      if (
        campaignResponse?.data?.internalCampaigns &&
        campaignResponse.data.internalCampaigns[internalCampaignIndex]
      ) {
        const originalData =
          campaignResponse.data.internalCampaigns[internalCampaignIndex];
        if (originalData.endDate) {
          originalInternalEndDate = dayjs(originalData.endDate);
        }
      }

      const error = validateInternalCampaignEndDate(
        newEndDate,
        formData.endDate ?? null,
        updatedInternalCampaigns[internalCampaignIndex].displayName,
        originalInternalEndDate
      );

      if (error) {
        setErrors((prev) => ({
          ...prev,
          [errorKey]: error,
        }));
      }
    }
  };

  // Date validation helper functions
  const validateEndDate = (endDate: dayjs.Dayjs | null): string | null => {
    if (!endDate) return null;

    // Check if the new end date is before the original end date
    // Only if we have campaign data from the API
    // Allow same date (no change) but prevent going backwards
    if (campaignResponse?.data?.endDate) {
      const originalEndDate = dayjs(campaignResponse.data.endDate);
      if (endDate.isBefore(originalEndDate, "day")) {
        return t("validation.endDateMustBeExtended", {
          defaultValue:
            "End date can only be extended beyond the original end date",
        });
      }
    }

    return null;
  };

  const validateInternalCampaignEndDate = (
    internalEndDate: dayjs.Dayjs | null,
    campaignEndDate: dayjs.Dayjs | null,
    internalCampaignName: string,
    originalInternalEndDate: dayjs.Dayjs | null
  ): string | null => {
    if (!internalEndDate) return null;

    // Check if internal campaign end date is not after campaign end date
    if (campaignEndDate && internalEndDate.isAfter(campaignEndDate)) {
      return t("validation.internalEndDateCannotExceedCampaign", {
        defaultValue:
          "Internal campaign end date cannot be after campaign end date",
        campaignName: internalCampaignName,
      });
    }

    // Check if the new internal end date is before the original internal end date
    // Allow same date (no change) but prevent going backwards
    if (
      originalInternalEndDate &&
      internalEndDate.isBefore(originalInternalEndDate)
    ) {
      return t("validation.internalEndDateMustBeExtended", {
        defaultValue:
          "Internal campaign end date can only be extended beyond the original end date",
        campaignName: internalCampaignName,
      });
    }

    return null;
  };

  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.displayName) {
      newErrors.displayName = t("validation.required");
    }

    // Validate campaign end date
    const campaignEndDateError = validateEndDate(formData.endDate ?? null);
    if (campaignEndDateError) {
      newErrors.campaignEndDate = campaignEndDateError;
    }

    // Validate internal campaign end dates
    formData.internalCampaigns.forEach((internalCampaign, index) => {
      // Get the original end date from the API response
      let originalInternalEndDate = null;
      if (
        campaignResponse?.data?.internalCampaigns &&
        campaignResponse.data.internalCampaigns[index]
      ) {
        const originalData = campaignResponse.data.internalCampaigns[index];
        if (originalData.endDate) {
          originalInternalEndDate = dayjs(originalData.endDate);
        }
      }

      const internalEndDateError = validateInternalCampaignEndDate(
        internalCampaign.endDate ?? null,
        formData.endDate ?? null,
        internalCampaign.displayName,
        originalInternalEndDate
      );
      if (internalEndDateError) {
        newErrors[`internalCampaign_${index}_endDate`] = internalEndDateError;
      }
    });

    setErrors(newErrors);
    const hasErrors = Object.keys(newErrors).length > 0;

    if (formData.platform === "DV360") {
      if (!selectedDv360CampaignId) {
        setDv360InsertionOrderError(
          t("campaigns.errors.dv360CampaignIdRequired", {
            defaultValue:
              "Select a DV360 campaign before choosing insertion orders.",
            campaign: formData.displayName,
          })
        );
        return false;
      }

      if (selectedDv360InsertionOrderIds.length === 0) {
        setDv360InsertionOrderError(
          t("campaigns.errors.dv360InsertionOrdersRequired", {
            defaultValue: "Select at least one insertion order.",
            campaign: formData.displayName,
          })
        );
        return false;
      }
      setDv360InsertionOrderError("");
    }

    return !hasErrors;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !campaignId) {
      console.log("Form validation failed or no campaignId");
      return;
    }

    try {
      // Create a properly typed update payload that matches the expected API structure
      const updatePayload = {
        displayName: formData.displayName,
        // Include format field for DV360 campaigns
        format: formData.format,
        // Include campaign endDate in the payload
        endDate: formData.endDate
          ? formData.endDate.format("YYYY-MM-DD")
          : undefined,
        dv360InsertionOrderIds:
          formData.platform === "DV360"
            ? selectedDv360InsertionOrderIds
            : undefined,
        internalCampaigns: formData.internalCampaigns.map((ic) => {
          const resolveInternalPlatform = (): InternalPlatform => {
            const rawInternalPlatform = ic.internalPlatform as
              | string
              | undefined;
            const rawPlatform = ic.platform as string | undefined;
            const candidate = rawInternalPlatform ?? rawPlatform;

            if (candidate?.startsWith("DV360")) {
              return "DV360";
            }

            // La plataforma interna que ya tiene se conserva. Antes cualquier otra que
            // no fuera Taboola acababa en OUTBRAIN al guardar: una subcampaña de Meta,
            // TikTok, Google Ads o Reddit pasaba a sincronizarse como Outbrain.
            if (rawInternalPlatform) {
              return rawInternalPlatform as InternalPlatform;
            }

            switch (candidate) {
              case "TABOOLA":
                return "TABOOLA";
              case "OUTBRAIN":
                return "OUTBRAIN";
              case "BEAPOLO":
                return "OUTBRAIN";
              default:
                return "OUTBRAIN";
            }
          };

          const normalizedInternalPlatform = resolveInternalPlatform();

          return {
            id: ic.id,
            displayName: ic.displayName || "",
            internalPlatform: normalizedInternalPlatform,
            internalId: ic.internalId,
            advertiserId: ic.advertiserId,
            margin: ic.margin,
            startDate: ic.startDate
              ? ic.startDate.format("YYYY-MM-DD")
              : undefined,
            endDate: ic.endDate ? ic.endDate.format("YYYY-MM-DD") : undefined,
          };
        }),
      };

      await updateCampaign({
        campaignId,
        updates: updatePayload,
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error updating campaign:", error);
    }
  };

  useEffect(() => {
    if (updateData) {
      dispatch(
        setToastMessage({
          message: t("campaigns.updateSuccess"),
          severity: "success",
        })
      );
      navigate(campaignId ? `/campaigns/${campaignId}` : "/campaigns");
    } else if (updateError) {
      const errorMessage =
        updateError && typeof updateError === "object" && "data" in updateError
          ? `${t("campaigns.updateError")}: ${
              (updateError as ApiErrorResponse).data?.message || ""
            }`
          : t("campaigns.updateError");

      dispatch(
        setToastMessage({
          message: errorMessage,
          severity: "error",
        })
      );
    }
  }, [updateData, updateError, dispatch, navigate, t, campaignId]);

  // Back goes to the campaign's own page, which is this editor's parent route.
  const handleBack = () => {
    navigate(campaignId ? `/campaigns/${campaignId}` : "/campaigns");
  };

  // Navigate to IO custom names editor
  const handleEditIONames = () => {
    if (campaignId) {
      navigate(`/campaigns/${campaignId}/insertion-orders/edit-names`);
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />

      <Box
        sx={{
          flexGrow: 1,
          px: 4,
          py: 2,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                variant="outlined"
                sx={{
                  borderRadius: 50,
                  textTransform: "none",
                  fontFamily: "Nunito",
                  borderColor: theme.palette.grey[200],
                  color: theme.palette.grey[700],
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: "transparent",
                  },
                }}
              >
                {t("common.back")}
              </Button>
              <Box sx={{ display: "flex", gap: 1 }}>
                {hasIOs && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleEditIONames}
                    sx={{
                      borderRadius: 50,
                      textTransform: "none",
                      fontFamily: "Nunito",
                    }}
                  >
                    {t("campaigns.editIONames", {
                      defaultValue: "Edit IO Names",
                    })}
                  </Button>
                )}
                <Button
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  variant="contained"
                  color="primary"
                  disabled={isUpdating}
                  sx={{
                    borderRadius: 50,
                    textTransform: "none",
                    fontFamily: "Nunito",
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                    },
                  }}
                >
                  {isUpdating ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("common.save")
                  )}
                </Button>
              </Box>
            </Box>
          </Box>

          {isLoadingCampaign ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {/* Campaign Name (editable) */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                    }}
                  >
                    {t("campaigns.name")}
                  </Typography>
                  <MuiTextField
                    name="displayName"
                    placeholder={t("campaigns.name")}
                    value={formData.displayName}
                    onChange={handleInputChange}
                    fullWidth
                    error={!!errors.displayName}
                    helperText={errors.displayName}
                    required
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 50,
                        fontFamily: "Nunito",
                        fontSize: 16,
                        "& fieldset": {
                          borderColor: theme.palette.grey[200],
                        },
                      },
                    }}
                  />
                </Box>

                <Grid container spacing={3}>
                  {/* Platform (read-only) */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Nunito",
                          fontWeight: 700,
                          color: theme.palette.grey[900],
                        }}
                      >
                        {t("campaigns.platform")}
                      </Typography>
                      <MuiTextField
                        name="platform"
                        value={getPlatformDisplayName(formData.platform || "")}
                        fullWidth
                        disabled
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 50,
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor: theme.palette.grey[200],
                            },
                            "&.Mui-disabled": {
                              backgroundColor: theme.palette.grey[100],
                            },
                          },
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Format dropdown - only shown for DV360 */}
                  {formData.platform === "DV360" && (
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Nunito",
                            fontWeight: 700,
                            color: theme.palette.grey[900],
                          }}
                        >
                          {t("campaigns.format")}
                        </Typography>
                        <FormControl
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 50,
                              fontFamily: "Nunito",
                              fontSize: 16,
                              "& fieldset": {
                                borderColor: theme.palette.grey[200],
                              },
                            },
                          }}
                        >
                          <Select
                            name="format"
                            value={formData.format || ""}
                            onChange={handleFormatChange}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return (
                                  <Typography
                                    sx={{
                                      color: theme.palette.grey[500],
                                      fontFamily: "Nunito",
                                    }}
                                  >
                                    {t("campaigns.selectFormat")}
                                  </Typography>
                                );
                              }
                              return selected;
                            }}
                          >
                            {isLoadingCampaignFormats ? (
                              <MenuItem disabled>
                                <CircularProgress size={20} />
                              </MenuItem>
                            ) : (
                              campaignFormatsData?.data?.formats.map(
                                (format) => (
                                  <MenuItem key={format} value={format}>
                                    {format}
                                  </MenuItem>
                                )
                              )
                            )}
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid>
                  )}

                  {formData.platform === "DV360" && (
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Nunito",
                            fontWeight: 700,
                            color: theme.palette.grey[900],
                          }}
                        >
                          {t("campaigns.dv360InsertionOrders", {
                            defaultValue: "DV360 Insertion Orders",
                          })}
                        </Typography>
                        <FormControl
                          fullWidth
                          error={Boolean(dv360InsertionOrderError)}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 50,
                              fontFamily: "Nunito",
                              fontSize: 16,
                              "& fieldset": {
                                borderColor: theme.palette.grey[200],
                              },
                            },
                          }}
                        >
                          {isLoadingDv360InsertionOrders ? (
                            <Box display="flex" justifyContent="center" py={1}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : isDv360InsertionOrdersError ? (
                            <FormHelperText>
                              {t(
                                "campaigns.errors.failedToLoadInsertionOrders",
                                {
                                  defaultValue:
                                    "Failed to load insertion orders.",
                                }
                              )}
                            </FormHelperText>
                          ) : dv360InsertionOrders.length === 0 ? (
                            // Estado vacío: el backend respondió OK pero no hay
                            // órdenes que mostrar (campaña sin internal campaign
                            // DV360 vinculada, o DV360 no devuelve ninguna IO).
                            // Antes esto dejaba el desplegable en blanco.
                            <FormHelperText sx={{ ml: 0 }}>
                              {t("campaigns.noInsertionOrdersAvailable", {
                                defaultValue:
                                  "No insertion orders available for this campaign. Make sure the campaign has a linked DV360 internal campaign with its ID.",
                              })}
                            </FormHelperText>
                          ) : (
                            <Select
                              multiple
                              value={selectedDv360InsertionOrderIds}
                              onChange={(event) => {
                                const value = event.target.value;
                                const nextSelections = Array.isArray(value)
                                  ? (value as string[])
                                  : [];
                                setSelectedDv360InsertionOrderIds(
                                  nextSelections
                                );
                                setSelectedDv360InsertionOrders(
                                  mapIdsToSelectedInfo(
                                    nextSelections,
                                    dv360InsertionOrders,
                                    selectedDv360InsertionOrders
                                  )
                                );
                                if (nextSelections.length > 0) {
                                  setDv360InsertionOrderError("");
                                }
                              }}
                              displayEmpty
                              renderValue={(selected) => {
                                const selectedIds = selected as string[];
                                if (selectedIds.length === 0) {
                                  return (
                                    <Typography
                                      sx={{
                                        color: theme.palette.grey[500],
                                        fontFamily: "Nunito",
                                      }}
                                    >
                                      {t("campaigns.selectInsertionOrders", {
                                        defaultValue: "Select insertion orders",
                                      })}
                                    </Typography>
                                  );
                                }
                                const selectedNames = dv360InsertionOrders
                                  .filter((order) =>
                                    selectedIds.includes(
                                      order?.insertionOrderId ?? ""
                                    )
                                  )
                                  .map(
                                    (order) =>
                                      order?.displayName ||
                                      order?.insertionOrderId
                                  )
                                  .filter((name): name is string =>
                                    Boolean(name)
                                  );
                                return selectedNames.join(", ");
                              }}
                            >
                              {dv360InsertionOrders.map((order) => {
                                const value = order?.insertionOrderId ?? "";
                                const isSelected =
                                  selectedDv360InsertionOrderIds.includes(
                                    value
                                  );
                                return (
                                  <MenuItem key={value} value={value} dense>
                                    <Checkbox
                                      checked={isSelected}
                                      size="small"
                                    />
                                    <ListItemText
                                      primary={order?.displayName || value}
                                      secondary={value}
                                    />
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          )}
                          {dv360InsertionOrderError && (
                            <FormHelperText>
                              {dv360InsertionOrderError}
                            </FormHelperText>
                          )}
                        </FormControl>
                        {selectedDv360InsertionOrders.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              mt: 1,
                            }}
                          >
                            {selectedDv360InsertionOrders.map((order) => (
                              <Chip
                                key={order.insertionOrderId}
                                label={
                                  order.displayName || order.insertionOrderId
                                }
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  )}

                  {/* Goal (read-only) */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Nunito",
                          fontWeight: 700,
                          color: theme.palette.grey[900],
                        }}
                      >
                        {t("campaigns.goal")}
                      </Typography>
                      <MuiTextField
                        name="goal"
                        value={formData.goal}
                        fullWidth
                        disabled
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 50,
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor: theme.palette.grey[200],
                            },
                            "&.Mui-disabled": {
                              backgroundColor: theme.palette.grey[100],
                            },
                          },
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Dates (read-only) */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Nunito",
                          fontWeight: 700,
                          color: theme.palette.grey[900],
                        }}
                      >
                        {t("campaigns.startDate")}
                      </Typography>
                      <MuiTextField
                        name="startDate"
                        value={formData.startDate?.format("YYYY-MM-DD") || ""}
                        fullWidth
                        disabled
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 50,
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor: theme.palette.grey[200],
                            },
                            "&.Mui-disabled": {
                              backgroundColor: theme.palette.grey[100],
                            },
                          },
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Nunito",
                          fontWeight: 700,
                          color: theme.palette.grey[900],
                        }}
                      >
                        {t("campaigns.endDate")}
                      </Typography>
                      <MuiTextField
                        name="endDate"
                        type="date"
                        value={formData.endDate?.format("YYYY-MM-DD") || ""}
                        onChange={handleCampaignEndDateChange}
                        fullWidth
                        variant="outlined"
                        error={!!errors.campaignEndDate}
                        helperText={errors.campaignEndDate}
                        inputProps={{
                          min: campaignResponse?.data?.endDate
                            ? dayjs(campaignResponse.data.endDate).format(
                                "YYYY-MM-DD"
                              )
                            : "",
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 50,
                            fontFamily: "Nunito",
                            fontSize: 16,
                            "& fieldset": {
                              borderColor: errors.campaignEndDate
                                ? theme.palette.error.main
                                : theme.palette.grey[200],
                            },
                          },
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Specialized Campaign Merge Editor for Edit Page */}
                  <Grid item xs={12} key={"campaign-merge-editor"}>
                    <Box sx={{ mt: 3 }}>
                      <CampaignMergeEditor
                        internalCampaigns={internalCampaigns}
                        merges={internalCampaignMerges}
                        onUpdateMerges={(newMerges) => {
                          setInternalCampaignMerges(newMerges);
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Internal Campaigns Section */}
                  {formData.internalCampaigns.map((internalCampaign, index) => (
                    <Grid item xs={12} key={internalCampaign.id || index}>
                      <Box sx={{ mt: 4, mb: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Montserrat",
                            fontWeight: 600,
                            color: theme.palette.grey[900],
                          }}
                        >
                          {t("campaigns.internalCampaign")}:{" "}
                          <span style={{ fontWeight: 700 }}>
                            {internalCampaign.displayName}
                          </span>
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                      </Box>

                      {/* Internal Campaign Details (read-only) */}
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                            >
                              {t("campaigns.internalPlatform")}
                            </Typography>
                            <MuiTextField
                              value={getPlatformDisplayName(
                                internalCampaign.internalPlatform || ""
                              )}
                              fullWidth
                              disabled
                              variant="outlined"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 50,
                                  fontFamily: "Nunito",
                                  fontSize: 16,
                                  "& fieldset": {
                                    borderColor: theme.palette.grey[200],
                                  },
                                  "&.Mui-disabled": {
                                    backgroundColor: theme.palette.grey[100],
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                            >
                              {t("campaigns.startDate")}
                            </Typography>
                            <MuiTextField
                              value={
                                internalCampaign.startDate?.format(
                                  "YYYY-MM-DD"
                                ) || ""
                              }
                              fullWidth
                              disabled
                              variant="outlined"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 50,
                                  fontFamily: "Nunito",
                                  fontSize: 16,
                                  "& fieldset": {
                                    borderColor: theme.palette.grey[200],
                                  },
                                  "&.Mui-disabled": {
                                    backgroundColor: theme.palette.grey[100],
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                            >
                              {t("campaigns.endDate")}
                            </Typography>
                            <MuiTextField
                              type="date"
                              value={
                                internalCampaign.endDate?.format(
                                  "YYYY-MM-DD"
                                ) || ""
                              }
                              onChange={(e) =>
                                handleInternalCampaignEndDateChange(e, index)
                              }
                              fullWidth
                              variant="outlined"
                              error={
                                !!errors[`internalCampaign_${index}_endDate`]
                              }
                              helperText={
                                errors[`internalCampaign_${index}_endDate`]
                              }
                              inputProps={{
                                min: campaignResponse?.data
                                  ?.internalCampaigns?.[index]?.endDate
                                  ? dayjs(
                                      campaignResponse.data.internalCampaigns[
                                        index
                                      ].endDate
                                    ).format("YYYY-MM-DD")
                                  : "",
                                max:
                                  formData.endDate?.format("YYYY-MM-DD") ||
                                  undefined, // Cannot exceed campaign end date
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 50,
                                  fontFamily: "Nunito",
                                  fontSize: 16,
                                  "& fieldset": {
                                    borderColor: errors[
                                      `internalCampaign_${index}_endDate`
                                    ]
                                      ? theme.palette.error.main
                                      : theme.palette.grey[200],
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Campaign Details Section */}
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                          {t("campaigns.details")}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t("campaigns.detailsDescription") ||
                            "Configure the details for this internal campaign"}
                        </Typography>
                      </Grid>

                      {/* Campaign Details Fields */}
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          ></Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          ></Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                            >
                              {t("campaigns.margin")}
                            </Typography>
                            <MuiTextField
                              name="margin"
                              type="number"
                              value={internalCampaign.margin || ""}
                              disabled
                              onChange={(e) =>
                                handleInternalCampaignChange(e, index)
                              }
                              fullWidth
                              variant="outlined"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 50,
                                  fontFamily: "Nunito",
                                  fontSize: 16,
                                  "& fieldset": {
                                    borderColor: theme.palette.grey[200],
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </form>
          )}
        </PageContainer>
      </Box>
    </Box>
  );
};

export default EditCampaign;
